/* global React, Icon, Card, Button, Modal, Segmented, KPI, toast, fmt, fmt0 */
// ============================================================
// PLUMME 3 — Privat økonomi · Gjeld (lån og nedbetaling)
// Lånene lagres via backend (upsert/deletePersonalFinanceLoan).
// Nedbetalingsplan, prioritering og simulator regnes lokalt fra
// restgjeld, rente og løpetid — ingen eksterne datakilder.
//
// Merk: designet sammenligner renten mot en markedsrente. Plumme
// har ingen rentekilde ennå, så vurderingen bygger kun på lånets
// eget rentenivå — vi viser ikke en markedsrente vi ikke har.
// ============================================================
const { useState: gjUseState, useMemo: gjUseMemo, useEffect: gjUseEffect } = React;

const GJ_COLORS = ['var(--plum-700)', 'var(--plum-500)', 'var(--plum-400)', 'var(--warning)', 'var(--success)', 'var(--plum-300)', 'var(--muted-2)'];
const GJ_ICONS = ['card', 'home', 'car', 'bank'];
const gjPct = (value) => `${Number(value || 0).toLocaleString(PlummeI18n.intlLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`;

// Annuitets-terminbeløp (fast månedlig betaling).
function gjAnnuity(principal, ratePct, months) {
  const rate = Number(ratePct || 0) / 100 / 12;
  const n = Math.max(1, Number(months || 1));
  if (!principal) return 0;
  if (rate <= 0) return principal / n;
  return principal * rate / (1 - Math.pow(1 + rate, -n));
}
const gjMethod = (loan) => loan.method || (/serie/i.test(loan.type || '') ? 'serie' : 'annuitet');

// Nedbetaling som tar hensyn til lånetype og en ekstra månedlig innbetaling.
function gjRun(loan, extra = 0) {
  const principal = Math.max(0, Number(loan.balance || 0));
  const rate = Number(loan.rate || 0) / 100 / 12;
  const n = Math.max(1, Number(loan.term || 240));
  const method = gjMethod(loan);
  const series = [[0, principal]];
  let balance = principal;
  let interest = 0;
  let month = 0;
  if (method === 'serie') {
    const principalPart = principal / n;
    const payment = principalPart + extra;
    const basePayment = principalPart + principal * rate;
    if (payment <= 0.01) return { basePmt: basePayment, firstPmt: payment + principal * rate, interest: Infinity, method, months: Infinity, series };
    while (balance > 0.01 && month < 1200) {
      interest += balance * rate;
      balance -= payment;
      month += 1;
      if (balance < 0) balance = 0;
      series.push([month, balance]);
    }
    return { basePmt: basePayment, firstPmt: payment + principal * rate, interest, method, months: month, series };
  }
  const annuity = gjAnnuity(principal, loan.rate, n);
  const payment = annuity + extra;
  if (payment <= principal * rate + 0.01) return { basePmt: annuity, firstPmt: payment, interest: Infinity, method, months: Infinity, series };
  while (balance > 0.01 && month < 1200) {
    const monthInterest = balance * rate;
    interest += monthInterest;
    balance = balance + monthInterest - payment;
    month += 1;
    if (balance < 0) balance = 0;
    series.push([month, balance]);
  }
  return { basePmt: annuity, firstPmt: payment, interest, method, months: month, series };
}

function gjYears(months) {
  if (!Number.isFinite(months)) return t('pkn.gj.never');
  const years = Math.floor(months / 12);
  const rest = Math.round(months % 12);
  if (!years) return t('pkn.gj.monthsShort', { count: rest });
  return `${t('pkn.gj.yearsShort', { count: years })}${rest ? ` ${t('pkn.gj.monthsShort', { count: rest })}` : ''}`;
}
function gjFreeDate(months) {
  if (!Number.isFinite(months)) return '—';
  const date = new Date();
  date.setMonth(date.getMonth() + Math.round(months));
  return date.toLocaleDateString(PlummeI18n.intlLocale(), { month: 'short', year: 'numeric' });
}

// Vurdering av rentenivået på lånet isolert sett.
function gjHealth(loan) {
  const rate = Number(loan.rate || 0);
  if (rate >= 12) return { key: 'dyr', label: t('pkn.gj.health.expensiveLabel'), color: 'var(--error)', bg: 'var(--error-bg)', icon: 'alertCircle', note: t('pkn.gj.health.expensiveNote') };
  if (rate >= 8) return { key: 'hoy', label: t('pkn.gj.health.highLabel'), color: 'var(--warning)', bg: 'var(--warning-bg)', icon: 'trendUp', note: t('pkn.gj.health.highNote') };
  if (rate <= 4) return { key: 'lav', label: t('pkn.gj.health.lowLabel'), color: 'var(--success)', bg: 'var(--success-bg)', icon: 'checkCircle', note: t('pkn.gj.health.lowNote') };
  return { key: 'grei', label: t('pkn.gj.health.okLabel'), color: 'var(--plum-700)', bg: 'var(--plum-100)', icon: 'checkCircle', note: t('pkn.gj.health.okNote') };
}

// Anbefalt ekstra innbetaling — minste beløp som gir minst 12 måneder spart.
function gjRecommend(loan) {
  const base = gjRun(loan, 0);
  if (!Number.isFinite(base.months)) return null;
  let best = null;
  for (const extra of [500, 1000, 1500, 2000, 3000]) {
    const alt = gjRun(loan, extra);
    if (!Number.isFinite(alt.months)) continue;
    const candidate = { extra, interest: base.interest - alt.interest, months: alt.months, saved: base.months - alt.months };
    if (!best || candidate.saved > best.saved) best = candidate;
    if (candidate.saved >= 12) { best = candidate; break; }
  }
  return best;
}

// Milepæler frem mot gjeldfri, basert på dagens planer.
function gjJourney(loans) {
  const events = [];
  loans.forEach((loan) => {
    const simulation = gjRun(loan, 0);
    const months = Number.isFinite(simulation.months) ? simulation.months : Number(loan.term || 0);
    events.push({ color: loan.color, icon: 'checkCircle', kind: 'payoff', label: t('pkn.gj.journey.paidOff', { name: loan.name }), months });
    if (/bolig/i.test(`${loan.category || ''}${loan.name || ''}`) && loan.balance > 500000) {
      const half = simulation.series.find((point) => point[1] <= loan.balance * 0.5);
      if (half) events.push({ color: loan.color, icon: 'trendDown', kind: 'half', label: t('pkn.gj.journey.half', { name: loan.name.toLowerCase() }), months: half[0] });
    }
  });
  events.sort((a, b) => a.months - b.months);
  if (events.length) events.push({ color: 'var(--success)', icon: 'sparkle', kind: 'free', label: t('pkn.gj.journey.debtFree'), months: Math.max(...events.map((event) => event.months)) });
  return events;
}

function GjChart({ alt, base, principal }) {
  const width = 820;
  const height = 240;
  const pad = { bottom: 28, left: 14, right: 14, top: 18 };
  const innerWidth = width - pad.left - pad.right;
  const innerHeight = height - pad.top - pad.bottom;
  const maxMonths = Math.max(base.series.length, alt.series.length, 2);
  const x = (month) => pad.left + innerWidth * month / (maxMonths - 1);
  const y = (value) => pad.top + innerHeight * (1 - value / (principal * 1.04));
  const path = (series) => series.map((point, index) => `${index ? 'L' : 'M'}${x(point[0]).toFixed(1)} ${y(point[1]).toFixed(1)}`).join(' ');
  const ticks = [0, principal * 0.5, principal].map((value) => Math.round(value));
  const yearStep = maxMonths / 12 > 24 ? 4 : maxMonths / 12 > 9 ? 2 : 1;
  const yearTicks = [];
  for (let month = 0; month <= maxMonths; month += yearStep * 12) yearTicks.push(month);
  const changed = base.series.length !== alt.series.length;
  const faster = Number.isFinite(alt.months) && Number.isFinite(base.months) && alt.months < base.months;
  const bandColor = faster ? 'var(--success)' : 'var(--warning)';
  const altPoints = alt.series.map((point) => [x(point[0]), y(point[1])]);
  const basePoints = base.series.map((point) => [x(point[0]), y(point[1])]);
  const band = `${altPoints.map((point, index) => `${index ? 'L' : 'M'}${point[0].toFixed(1)} ${point[1].toFixed(1)}`).join(' ')} ${basePoints.slice().reverse().map((point) => `L${point[0].toFixed(1)} ${point[1].toFixed(1)}`).join(' ')} Z`;
  const baseEnd = Number.isFinite(base.months) ? x(base.months) : null;
  const altEnd = Number.isFinite(alt.months) ? x(alt.months) : null;
  return <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="100%" preserveAspectRatio="none" style={{ display: 'block' }}>
    {ticks.map((tick, index) => <g key={index}>
      <line x1={pad.left} x2={width - pad.right} y1={y(tick)} y2={y(tick)} stroke="var(--border)" strokeWidth="1" />
      <text x={pad.left + 2} y={y(tick) - 5} fontSize="11.5" fontWeight="700" fill="var(--muted-2)">{fmt0(tick)}</text>
    </g>)}
    {yearTicks.map((month, index) => <text key={index} x={x(month)} y={height - 9} fontSize="11" fontWeight="700" fill="var(--muted-2)" textAnchor="middle">{month === 0 ? t('pkn.gj.now') : t('pkn.gj.yearsShort', { count: month / 12 })}</text>)}
    {changed && <path d={band} fill={bandColor} opacity="0.13" />}
    <path d={path(base.series)} fill="none" stroke="var(--muted-2)" strokeWidth="2.5" strokeDasharray="5 5" strokeLinecap="round" />
    <path d={path(alt.series)} fill="none" stroke={faster ? 'var(--success)' : 'var(--plum-600)'} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    {baseEnd !== null && <circle cx={baseEnd} cy={y(0)} r="4" fill="var(--card)" stroke="var(--muted-2)" strokeWidth="2" />}
    {altEnd !== null && changed && <g>
      <line x1={altEnd} x2={altEnd} y1={y(0)} y2={pad.top + 6} stroke={bandColor} strokeWidth="1.5" strokeDasharray="3 3" opacity="0.7" />
      <circle cx={altEnd} cy={y(0)} r="5.5" fill={bandColor} stroke="var(--card)" strokeWidth="2" />
      <text x={altEnd} y={pad.top + 2} fontSize="11.5" fontWeight="900" fill={bandColor} textAnchor="middle">{t('sharedText.po.debtFree')}</text>
    </g>}
  </svg>;
}

function GjLoanForm({ loan, onClose, onDelete, onSave }) {
  const [draft, setDraft] = gjUseState(() => (loan
    ? { ...loan, method: gjMethod(loan) }
    : { balance: '', category: '', color: GJ_COLORS[0], icon: 'card', method: 'annuitet', name: '', original: '', rate: '', term: '', type: '' }));
  const set = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const field = (label, key, opts = {}) => <label className="gj-field">
    <span>{label}</span>
    <input className="gj-input" type={opts.type || 'text'} value={draft[key] ?? ''} placeholder={opts.placeholder || ''} onChange={(event) => set(key, event.target.value)} />
  </label>;
  const save = () => {
    if (!String(draft.name || '').trim() || !Number(draft.balance || 0) || draft.rate === '') {
      toast(t('pkn.gj.fillRequired'), 'error');
      return;
    }
    onSave({
      ...draft,
      balance: Number(draft.balance || 0),
      category: String(draft.category || '').trim() || String(draft.name || '').trim(),
      color: draft.color || GJ_COLORS[0],
      icon: draft.icon || 'card',
      id: draft.id || `loan-${Date.now()}`,
      name: String(draft.name || '').trim(),
      original: Number(draft.original || draft.balance || 0),
      rate: Number(draft.rate || 0),
      term: Number(draft.term || 240),
      type: String(draft.type || '').trim(),
    });
  };
  return <Modal open onClose={onClose} title={loan ? t('pkn.gj.editLoan') : t('pkn.gj.addLoan')} max={560}
    footer={<React.Fragment>
      {loan && onDelete && <Button variant="ghost" icon="trash" onClick={() => onDelete(loan)}>{t('list.delete')}</Button>}
      <div style={{ flex: 1 }} />
      <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
      <Button icon="check" onClick={save}>{t('po.saveLoan')}</Button>
    </React.Fragment>}>
    <div className="gj-form">
      {field(t('pkn.gj.fieldName'), 'name', { placeholder: t('pkn.gj.phName') })}
      {field(t('pkn.gj.fieldLender'), 'type', { placeholder: t('pkn.gj.phLender') })}
      <div className="gj-form-row">
        {field(t('pkn.gj.fieldBalance'), 'balance', { type: 'number', placeholder: '0' })}
        {field(t('pkn.gj.fieldOriginal'), 'original', { type: 'number', placeholder: t('pkn.gj.optionalLower') })}
      </div>
      <div className="gj-form-row">
        {field(t('pkn.gj.fieldNominal'), 'rate', { type: 'number', placeholder: '5,45' })}
        {field(t('pkn.gj.fieldEffective'), 'effectiveRate', { type: 'number', placeholder: t('pkn.gj.optionalUpper') })}
        {field(t('pkn.gj.fieldTerm'), 'term', { type: 'number', placeholder: '240' })}
      </div>
      <label className="gj-field" data-help={t('helpTopics.repaymentType.body')} data-help-title={t('helpTopics.repaymentType.title')}><span  >{t('po.loanType')}</span>
        <Segmented value={draft.method} onChange={(value) => set('method', value)} options={[{ label: t('pkn.gj.annuityLoan'), value: 'annuitet' }, { label: t('pkn.gj.serialLoan'), value: 'serie' }]} />
        <span className="gj-hint">{draft.method === 'serie'
          ? t('pkn.gj.serialHint')
          : t('pkn.gj.annuityHint')}</span>
      </label>
      <label className="gj-field"><span>{t('po.icon')}</span>
        <div className="gj-icon-pick">
          {GJ_ICONS.map((icon) => <button key={icon} type="button" className={`gj-icon-opt${draft.icon === icon ? ' on' : ''}`} title={t('pkn.gj.icon.' + icon)} onClick={() => set('icon', icon)}><Icon name={icon} size={18} /></button>)}
        </div>
      </label>
      <label className="gj-field"><span>{t('po.spendingCategory')}</span>
        <input className="gj-input" value={draft.category} placeholder={t('po.categoryPh')} onChange={(event) => set('category', event.target.value)} />
        <div className="gj-color-pick">
          {GJ_COLORS.map((color) => <button key={color} type="button" className={`gj-swatch${draft.color === color ? ' on' : ''}`} style={{ background: color }} onClick={() => set('color', color)} aria-label={t('pkn.eie.pickColor', { color })} />)}
        </div>
        <span className="gj-hint">{t('pkn.gj.postedAs', { category: draft.category || t('pkn.gj.categoryFallback') })}</span>
      </label>
    </div>
  </Modal>;
}

function GjLoanCard({ active, loan, onClick, onEdit }) {
  const payment = Number(loan.observedPayment || 0) || gjRun(loan, 0).firstPmt;
  const original = Number(loan.original || loan.balance || 0);
  const paid = original ? Math.max(0, Math.min(1, (original - loan.balance) / original)) : 0;
  const health = gjHealth(loan);
  return <div className={`gj-card${active ? ' on' : ''}`} role="button" tabIndex={0} onClick={onClick}
    onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onClick(); } }}>
    <div className="gj-card-top">
      <span className="gj-card-ico" style={{ background: loan.color }}><Icon name={loan.icon} size={19} /></span>
      <div className="gj-card-head">
        <div className="gj-card-name">{loan.name}</div>
        <div className="gj-card-type">{loan.type || t('pkn.gj.loanFallback')}</div>
      </div>
      <span className="gj-card-rate">{gjPct(loan.rate)}</span>
      <button className="aii-cost-more" onClick={(event) => { event.stopPropagation(); onEdit(); }} title={t('pkn.gj.editNamed', { name: loan.name })} aria-label={t('pkn.gj.editNamed', { name: loan.name })}><Icon name="pencil" size={15} /></button>
    </div>
    <div className="gj-card-bal">{fmt(loan.balance)}</div>
    <div className="gj-card-prog"><div style={{ width: `${paid * 100}%`, background: loan.color }} /></div>
    <div className="gj-card-foot">
      <span className="gj-health" style={{ color: health.color, background: health.bg }}><Icon name={health.icon} size={12} />{health.label}</span>
      <span className="gj-card-pmt" title={loan.observedPayment ? t('pkn.gj.observedIn', { count: loan.observedPaymentCount }) : t('pkn.gj.calculatedFrom')}>{t('pkn.fut.perMonth', { amount: fmt(Math.round(payment)) })}{loan.observedPayment ? ` ${t('pkn.gj.observed')}` : ''}</span>
    </div>
  </div>;
}

function PoGjeld({ cashflow = null, debtPayments = 0, debtTotal = null, loans = [], onDeleteLoan, onSaveLoan }) {
  const Sec = window.PoSecHead;
  const [selectedId, setSelectedId] = gjUseState('');
  const [extra, setExtra] = gjUseState(0);
  const [formLoan, setFormLoan] = gjUseState(null);

  const activeLoans = loans.filter((loan) => Number(loan.balance || 0) > 0);
  const selected = activeLoans.find((loan) => loan.id === selectedId) || activeLoans[0] || null;
  gjUseEffect(() => { setExtra(0); }, [selectedId]);

  const total = debtTotal === null || debtTotal === undefined
    ? activeLoans.reduce((sum, loan) => sum + Number(loan.balance || 0), 0)
    : Number(debtTotal || 0);
  const monthlyPayment = activeLoans.reduce((sum, loan) => sum + (Number(loan.observedPayment || 0) || gjRun(loan, 0).firstPmt), 0);
  const yearInterest = activeLoans.reduce((sum, loan) => sum + Number(loan.balance || 0) * Number(loan.rate || 0) / 100, 0);
  const avgRate = total > 0 ? activeLoans.reduce((sum, loan) => sum + Number(loan.balance || 0) * Number(loan.rate || 0), 0) / total : 0;
  const maxTerm = activeLoans.reduce((max, loan) => Math.max(max, Number(loan.term || 0)), 0);

  const simulation = gjUseMemo(() => (selected ? { alt: gjRun(selected, extra), base: gjRun(selected, 0) } : null), [selected, extra]);
  const basePayment = selected ? gjRun(selected, 0).basePmt : 0;
  const sliderMax = selected ? Math.max(5000, Math.round(basePayment / 500) * 500) : 5000;
  const sliderMin = selected ? -Math.round(basePayment * 0.5 / 250) * 250 : 0;
  const monthsSaved = simulation && Number.isFinite(simulation.alt.months) && Number.isFinite(simulation.base.months) ? simulation.base.months - simulation.alt.months : null;
  const interestSaved = simulation && Number.isFinite(simulation.alt.interest) && Number.isFinite(simulation.base.interest) ? simulation.base.interest - simulation.alt.interest : null;
  const recommendation = selected ? gjRecommend(selected) : null;
  const priority = [...activeLoans].sort((a, b) => Number(b.effectiveRate ?? b.rate ?? 0) - Number(a.effectiveRate ?? a.rate ?? 0));
  const journey = gjUseMemo(() => gjJourney(activeLoans), [activeLoans]);
  const avatar = (window.__resources && window.__resources.superPlummaHero) || 'src/img/SuperPlumma.png';

  return <React.Fragment>
    <div>
      <Sec icon="card" title={t('po.debtOverview')} sub={t('po.debtOverviewSub')} />
      <div className="grid-3" style={{ gap: 14, marginTop: 14 }} data-help-title={t('po.debtOverview')} data-help={t('po.debtOverviewHelp')}>
        <KPI tint="peach" icon="card" label={t('po.totalDebt')} value={total ? fmt(total) : t('pkn.eie.notRegistered')}  help={t('helpTopics.netWealth.body')}/>
        <KPI tint="white" icon="calendar" label={t('po.repaymentPerMonth')} value={fmt(Math.round(monthlyPayment || Number(debtPayments || 0)))}  help={t('helpTopics.loanTerms.body')}/>
        <KPI tint="peach" icon="percent" label={t('po.interestPerYear')} value={yearInterest ? fmt(Math.round(yearInterest)) : t('pkn.eie.notRegistered')}  help={t('helpTopics.interestRate.body')}/>
        <KPI tint="white" icon="trendDown" label={t('po.averageRate')} value={avgRate ? gjPct(avgRate) : t('pkn.eie.notRegistered')}  help={t('helpTopics.interestRate.body')}/>
        <KPI tint="green" icon="checkCircle" label={t('po.debtFreeIn')} value={maxTerm ? gjYears(maxTerm) : t('pkn.eie.notRegistered')}  help={t('helpTopics.loanTerms.body')}/>
        <KPI tint="plum" icon="bank" label={t('po.loanCount')} value={String(activeLoans.length)} />
      </div>
    </div>

    <Card>
      <Sec icon="list" title={t('po.yourLoans')} sub={t('po.yourLoansSub')}
        right={<Button size="sm" icon="plus" onClick={() => setFormLoan({})}>{t('po.addLoan')}</Button>} />
      {activeLoans.length === 0
        ? <div className="gj-empty">{t('po.noLoans')}</div>
        : <div className="gj-list" style={{ marginTop: 14 }}>
          {activeLoans.map((loan) => <GjLoanCard key={loan.id} loan={loan} active={selected && selected.id === loan.id} onClick={() => setSelectedId(loan.id)} onEdit={() => setFormLoan(loan)} />)}
        </div>}
    </Card>

    {activeLoans.length > 1 && <Card>
      <Sec icon="trendDown" title={t('po.whichLoanFirst')} sub={t('po.whichLoanFirstSub')} />
      <div className="gj-prio-list">
        {priority.map((loan, index) => {
          const health = gjHealth(loan);
          const priorityRate = Number(loan.effectiveRate ?? loan.rate ?? 0);
          const interest = Math.round(Number(loan.balance || 0) * priorityRate / 100);
          return <div key={loan.id} className="gj-prio" role="button" tabIndex={0} onClick={() => setSelectedId(loan.id)}
            onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setSelectedId(loan.id); } }}>
            <span className="gj-prio-rank" style={{ background: index === 0 ? 'var(--error)' : index === 1 ? 'var(--warning)' : 'var(--plum-400)' }}>{index + 1}</span>
            <span className="gj-prio-ico" style={{ background: loan.color }}><Icon name={loan.icon} size={17} /></span>
            <div className="gj-prio-body">
              <div className="gj-prio-name">{loan.name}<span className="gj-health" style={{ color: health.color, background: health.bg }}><Icon name={health.icon} size={11} />{health.label}</span></div>
              <div className="gj-prio-why">{index === 0
                ? t(loan.effectiveRate != null ? 'pkn.gj.highestEffective' : 'pkn.gj.highest', { rate: gjPct(priorityRate), interest: fmt(interest) })
                : t('pkn.gj.laterLoan', { rate: gjPct(priorityRate), interest: fmt(interest) })}</div>
            </div>
            <span className="gj-prio-rate" style={{ color: health.color }}>{gjPct(priorityRate)}</span>
          </div>;
        })}
      </div>
      <div className="aii-cf-commentary gj-plumma" style={{ background: 'var(--plum-50)', borderColor: 'var(--plum-200)', marginTop: 14 }}>
        <span className="gj-plumma-ava"><img src={avatar} alt="SuperPlumma" /></span>
        <p>{cashflow?.bufferGap > 0
          ? <React.Fragment>{t('pkn.gj.bufferFirst')} <b>{fmt(cashflow.bufferGap)}</b> {t('pkn.gj.bufferFirstSuffix')}</React.Fragment>
          : <React.Fragment>{t('sharedText.po.iRecommend')} <b>{t('po.snowballWithRate')}</b>: {t('pkn.gj.snowA')} <b>{priority[0].name.toLowerCase()}</b> {t('pkn.gj.snowB', { rate: gjPct(priority[0].effectiveRate ?? priority[0].rate) })}</React.Fragment>}</p>
      </div>
    </Card>}

    {selected && simulation && <Card>
      <Sec icon="sliders" title={t('pkn.gj.planTitle', { name: selected.name })}
        sub={t('po.repaymentSimSub')}
        right={<div className="row gap-8">
          <Button size="sm" variant="ghost" icon="pencil" onClick={() => setFormLoan(selected)}>{t('cust.act.edit')}</Button>
          <Button size="sm" variant="ghost" icon="trash" onClick={() => onDeleteLoan?.(selected)}>{t('list.delete')}</Button>
        </div>} />

      {recommendation && recommendation.saved > 0 && selected.id === priority[0]?.id && !cashflow?.bufferGap && <div className="gj-rec">
        <span className="gj-rec-ava"><img src={avatar} alt="SuperPlumma" /></span>
        <div className="gj-rec-body">
          <div className="gj-rec-tag"><Icon name="sparkle" size={14} />{t('po.myRecommendation')}</div>
          <p><b>{t('pkn.gj.extraPerMonth', { amount: fmt(recommendation.extra) })}</b> {t('pkn.gj.makesDebtFree', { name: selected.name.toLowerCase() })} <b>{t('pkn.gj.earlier', { time: gjYears(recommendation.saved) })}</b> {t('pkn.gj.andKeep')} <b>{fmt(Math.round(recommendation.interest))}</b> {t('pkn.gj.toBank')}</p>
        </div>
        <button className={`gj-rec-cta${extra === recommendation.extra ? ' on' : ''}`} onClick={() => setExtra(extra === recommendation.extra ? 0 : recommendation.extra)}>
          {extra === recommendation.extra ? t('pkn.gj.activated') : t('pkn.gj.tryRecommendation')}<Icon name={extra === recommendation.extra ? 'check' : 'arrowRight'} size={14} />
        </button>
      </div>}

      <div className="gj-chart-legend">
        <span><i className="gj-leg gj-leg-base" />{t('po.todaysPlan')}</span>
        <span><i className="gj-leg gj-leg-alt" />{t('po.withChange')}</span>
      </div>
      <div style={{ aspectRatio: '3.2 / 1', marginTop: 4, overflow: 'hidden' }}><GjChart base={simulation.base} alt={simulation.alt} principal={selected.balance} /></div>

      <div className="gj-sim">
        <div className="gj-sim-head">
          <div>
            <div className="gj-sim-lbl">{t('po.extraRepaymentPerMonth')}</div>
            <div className="gj-sim-sub">{t('po.dragToPayMore')}</div>
          </div>
          <div className={`gj-sim-amount${extra < 0 ? ' neg' : extra > 0 ? ' pos' : ''}`}>{extra > 0 ? '+' : ''}{fmt(extra)}</div>
        </div>
        <input className="gj-slider" type="range" min={sliderMin} max={sliderMax} step={250} value={extra} onChange={(event) => setExtra(Number(event.target.value || 0))} />
        <div className="gj-slider-ends"><span>{fmt(sliderMin)}</span><span>{t('po.standard')}</span><span>+{fmt(sliderMax)}</span></div>
        <div className="row gap-8" style={{ flexWrap: 'wrap', marginTop: 10 }}>
          {[0, 1000, 2500, 5000].map((value) => <button key={value} className={`gj-preset${extra === value ? ' on' : ''}`} onClick={() => setExtra(value)}>{value === 0 ? t('pkn.gj.standard') : `+${fmt0(value)} kr`}</button>)}
        </div>
      </div>

      <div className={`gj-hero${monthsSaved > 0 ? ' good' : monthsSaved < 0 ? ' slow' : ''}`}>
        <div className="gj-hero-l">
          <div className="gj-hero-eyebrow"><Icon name="sparkle" size={15} />{extra > 0 ? t('pkn.gj.heroEarlier') : extra < 0 ? t('pkn.gj.heroLonger') : t('pkn.gj.heroDrag')}</div>
          <div className="gj-hero-big">
            {!monthsSaved
              ? gjFreeDate(simulation.alt.months)
              : <React.Fragment>{gjYears(Math.abs(monthsSaved))} <span>{monthsSaved > 0 ? t('pkn.gj.earlierWord') : t('pkn.gj.laterWord')}</span></React.Fragment>}
          </div>
          <div className="gj-hero-sub">{t('sharedText.po.debtFree')} <b>{gjFreeDate(simulation.base.months)}</b><Icon name="arrowRight" size={14} /><b className="hl">{gjFreeDate(simulation.alt.months)}</b></div>
        </div>
        <div className="gj-hero-r">
          <div className="gj-hero-r-lbl">{interestSaved >= 0 ? t('pkn.gj.interestSaved') : t('pkn.gj.interestMore')}</div>
          <div className="gj-hero-r-val">{interestSaved === null ? '—' : `${interestSaved >= 0 ? '' : '−'}${fmt(Math.abs(Math.round(interestSaved)))}`}</div>
        </div>
      </div>

      <div className="gj-result">
        <div className="gj-res">
          <div className="gj-res-lbl">{simulation.base.method === 'serie' ? t('pkn.gj.paymentNow') : t('pkn.gj.newMonthlyPayment')}</div>
          <div className="gj-res-val">{fmt(Math.round(basePayment + extra))}</div>
          <div className="gj-res-note">{simulation.base.method === 'serie' ? t('pkn.gj.declines') : t('pkn.gj.standardAmount', { amount: fmt(Math.round(basePayment)) })}</div>
        </div>
        <div className="gj-res">
          <div className="gj-res-lbl">{t('sharedText.po.debtFree')}</div>
          <div className="gj-res-val">{gjFreeDate(simulation.alt.months)}</div>
          <div className="gj-res-note">{t('pkn.gj.timeLeft', { time: gjYears(simulation.alt.months) })}</div>
        </div>
        <div className="gj-res">
          <div className="gj-res-lbl">{t('po.savedInInterest')}</div>
          <div className="gj-res-val" style={{ color: interestSaved > 0 ? 'var(--success)' : interestSaved < 0 ? 'var(--error)' : 'var(--ink)' }}>
            {interestSaved === null ? '—' : `${interestSaved >= 0 ? '' : '−'}${fmt(Math.abs(Math.round(interestSaved)))}`}
          </div>
          <div className="gj-res-note">{t('po.overWholeTerm')}</div>
        </div>
        <div className="gj-res">
          <div className="gj-res-lbl">{t('po.netWealthEffect')}</div>
          <div className="gj-res-val" style={{ color: interestSaved > 0 ? 'var(--success)' : interestSaved < 0 ? 'var(--error)' : 'var(--ink)' }}>
            {interestSaved === null ? '—' : `${interestSaved >= 0 ? '+' : '−'}${fmt(Math.abs(Math.round(interestSaved)))}`}
          </div>
          <div className="gj-res-note">{t('po.becomesYours')}</div>
        </div>
      </div>

      <div className="gj-cat-note">
        <span className="gj-chip" style={{ background: selected.color }}>{selected.category}</span>
        <span>{t('sharedText.po.interestPostedAs')} <b>«{selected.category}»</b> {t('pkn.gj.inSpendingSuffix')}</span>
      </div>
    </Card>}

    {journey.length > 0 && <Card>
      <Sec icon="sparkle" title={t('po.debtJourney')} sub={t('po.debtJourneySub')} />
      <div className="gj-journey">
        {journey.map((event, index) => <div key={index} className={`gj-journey-item${event.kind === 'free' ? ' free' : ''}`}>
          <div className="gj-journey-date">{gjFreeDate(event.months)}</div>
          <div className="gj-journey-rail"><span className="gj-journey-node" style={{ background: event.color }}><Icon name={event.icon} size={13} /></span>{index < journey.length - 1 && <span className="gj-journey-bar" />}</div>
          <div className="gj-journey-body"><span className="gj-journey-label">{event.label}</span><span className="gj-journey-in">{t('pkn.gj.timeLeft', { time: gjYears(event.months) })}</span></div>
        </div>)}
      </div>
    </Card>}

    {formLoan && <GjLoanForm
      loan={formLoan.id ? formLoan : null}
      onClose={() => setFormLoan(null)}
      onDelete={(loan) => { onDeleteLoan?.(loan); setFormLoan(null); }}
      onSave={(loan) => { onSaveLoan?.(loan); setSelectedId(loan.id); setFormLoan(null); }}
    />}
  </React.Fragment>;
}

Object.assign(window, { PoGjeld });
